import { useState, useEffect } from "react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Label } from "@/components/ui/label";
import { Loader2 } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";

interface VapiAccount {
  id: string;
  keyName: string;
  friendlyName: string;
  maskedKey: string;
  isPrimary: boolean;
}

interface VapiKeySelectorProps {
  value?: string;
  onChange: (value: string) => void;
  label?: string;
  description?: string;
  className?: string;
}

export function VapiKeySelector({ 
  value, 
  onChange, 
  label = "VAPI Account", 
  description = "Select which VAPI account to use for this campaign",
  className 
}: VapiKeySelectorProps) {
  const [accounts, setAccounts] = useState<VapiAccount[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Load VAPI accounts from backend (same as VapiAccountManager)
  useEffect(() => {
    loadAccounts();
  }, []);

  const loadAccounts = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const { data, error: fetchError } = await supabase.functions.invoke('manage-api-keys', {
        body: { 
          action: 'get', 
          filter_provider: 'vapi' 
        }
      });

      if (fetchError) throw fetchError;

      const vapiAccounts: VapiAccount[] = (data?.keys || []).map((k: any) => ({
        id: k.id,
        keyName: k.key_name,
        friendlyName: k.friendlyName || 'VAPI Account',
        maskedKey: maskApiKey(k.decrypted_value),
        isPrimary: k.key_name === 'api_key'
      }));

      // Sort primary first
      vapiAccounts.sort((a, b) => {
        if (a.isPrimary) return -1;
        if (b.isPrimary) return 1;
        return 0;
      });

      setAccounts(vapiAccounts);
    } catch (error) {
      console.error('Error loading VAPI accounts:', error);
      setError('Failed to load VAPI accounts');
    } finally {
      setLoading(false);
    }
  };

  const maskApiKey = (key: string): string => {
    if (!key || key.length < 8) return key;
    return key.substring(0, 4) + '•'.repeat(Math.max(0, key.length - 8)) + key.substring(key.length - 4);
  };

  // Default to first available key if no value is set
  const effectiveValue = value || (accounts.length > 0 ? accounts[0].keyName : '');

  // Auto-select first key if none selected and keys are available
  if (!value && accounts.length > 0 && effectiveValue) {
    onChange(effectiveValue);
  }

  if (loading) {
    return (
      <div className={`space-y-2 ${className}`}>
        <Label>{label}</Label>
        <div className="flex items-center gap-2 p-3 border rounded-lg">
          <Loader2 className="h-4 w-4 animate-spin" />
          <span className="text-sm text-muted-foreground">Loading VAPI accounts...</span>
        </div>
      </div>
    );
  }

  if (error || accounts.length === 0) {
    return (
      <div className={`space-y-2 ${className}`}>
        <Label>{label}</Label>
        <div className="p-3 border border-destructive/50 rounded-lg bg-destructive/5">
          <p className="text-sm text-destructive">
            ⚠️ {error || 'No VAPI accounts configured. Please add VAPI API keys in Settings first.'}
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className={`space-y-2 ${className}`}>
      <Label htmlFor="vapi-key">{label}</Label>
      <Select value={effectiveValue} onValueChange={onChange}>
        <SelectTrigger id="vapi-key">
          <SelectValue placeholder="Select VAPI account" />
        </SelectTrigger>
        <SelectContent>
          {accounts.map((account) => (
            <SelectItem key={account.keyName} value={account.keyName}>
              <div className="flex items-center gap-2">
                <span>{account.friendlyName}</span>
                <span className="text-xs text-muted-foreground font-mono">
                  {account.maskedKey}
                </span>
              </div>
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
      {description && (
        <p className="text-sm text-muted-foreground">{description}</p>
      )}
    </div>
  );
}